import { Component, Input } from '@angular/core';

export type FileTypeIconSize = 'sm' | 'md' | 'lg';
export type FileTypeVisualCategory =
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'presentation'
  | 'archive'
  | 'code'
  | 'text'
  | 'generic';

const FILE_TYPE_LABELS: Record<Exclude<FileTypeVisualCategory, 'generic'>, ReadonlySet<string>> = {
  pdf: new Set(['PDF']),
  document: new Set(['DOC', 'DOCX', 'ODT', 'RTF', 'WORD']),
  spreadsheet: new Set(['CSV', 'ODS', 'XLS', 'XLSX', 'EXCEL']),
  presentation: new Set(['ODP', 'PPT', 'PPTX', 'POWERPOINT']),
  archive: new Set(['7Z', 'BZ2', 'GZ', 'RAR', 'TAR', 'TGZ', 'ZIP']),
  code: new Set([
    'C', 'CPP', 'CSS', 'GO', 'HTML', 'JAVA', 'JS', 'JSON', 'JSX', 'PHP', 'PY',
    'RB', 'RS', 'SCSS', 'SH', 'SQL', 'TS', 'TSX', 'XML', 'YAML', 'YML',
  ]),
  text: new Set(['LOG', 'MARKDOWN', 'MD', 'TEXT', 'TXT']),
};

const GLYPH_PATHS: Record<FileTypeVisualCategory, string> = {
  pdf: 'M14 31c7-9 11-11 19-12M16 20c1 7 5 12 13 15M13 34c7-3 14-3 22-1',
  document: 'M14 19h20M14 25h20M14 31h14',
  spreadsheet: 'M14 18h20v15H14zM21 18v15M28 18v15M14 25.5h20',
  presentation: 'M14 18h20v14H14zM24 32v4M19 36h10M24 21v8l7-4z',
  archive: 'M22 17h4v4h-4zM22 23h4v4h-4zM22 29h4v4h-4zM19 36h10v-3H19z',
  code: 'M20 19l-6 7 6 7M28 19l6 7-6 7M27 17l-6 18',
  text: 'M15 18h18M24 18v16M18 34h12',
  generic: 'M15 24h4v4h-4zM22 24h4v4h-4zM29 24h4v4h-4z',
};

export function resolveFileTypeVisualCategory(
  typeLabel: string | null | undefined,
): FileTypeVisualCategory {
  const normalized = normalizeTypeLabel(typeLabel);
  const match = (Object.keys(FILE_TYPE_LABELS) as Exclude<FileTypeVisualCategory, 'generic'>[])
    .find((category) => FILE_TYPE_LABELS[category].has(normalized));
  return match ?? 'generic';
}

function normalizeTypeLabel(typeLabel: string | null | undefined): string {
  return (typeLabel ?? '').trim().replace(/^\./, '').toUpperCase();
}

@Component({
  standalone: false,
  selector: 'core-ui-file-type-icon',
  templateUrl: './file-type-icon.component.html',
  styleUrls: ['./file-type-icon.component.scss'],
})
export class CoreUIFileTypeIconComponent {
  @Input({ required: true }) typeLabel!: string;
  @Input() color: string | null = null;
  @Input() size: FileTypeIconSize = 'md';

  get visualCategory(): FileTypeVisualCategory {
    return resolveFileTypeVisualCategory(this.typeLabel);
  }

  get displayLabel(): string {
    return normalizeTypeLabel(this.typeLabel).slice(0, 5) || 'FILE';
  }

  get glyphPath(): string {
    return GLYPH_PATHS[this.visualCategory];
  }
}
